import { Button } from 'antd';
import clsx from 'clsx';
import { findIndex } from 'lodash';
import React, { useEffect, useState } from 'react';
import { toast } from '../../apollo';
import useLazyQueryWithCallbacks from '../../common/CommonUseLazyQuery';
import {
  DEFAULT_PAGE_SIZE,
  REGISTER_LOG_STATUS,
  SCROLL_PERCENT,
} from '../../common/constants';
import useRouter from '../../common/useRouter';
import {
  GET_REGISTER,
  GET_REGISTER_LOG_DETAILS_ENTRY,
  GET_REGISTER_LOG_FORM_FIELDS_ENTRY_LIST,
} from '../projects/graphql/Queries';
import { GET_REGISTER_LOG_TO_VALIDATE_APPROVAL } from './graphql/Queries';
import RegisterAdditionalComment from './RegisterAdditionalComment';
import RegisterApprovalPopUp from './RegisterApprovalPopUp';
import RegisterApprovalSummary from './RegisterApprovalSummary';
import RegisterApproveModal from './RegisterApproveModal';
import RegisterLogDetailsSection from './RegisterLogDetailsSection';
import RegisterLogEntriesSection from './RegisterLogEntriesSection';

const RegisterApproverLink = () => {
  const {
    params: { registerId, logId },
  } = useRouter();

  const [showModal, setShowModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [title, setTitle] = useState('');
  const [status, setStatus] = useState('');
  const [commentData, setCommentData] = useState({
    comments: [],
  });
  const [additionalComments, setAdditionalComments] = useState([]);

  // State for register data
  const [registerData, setRegisterData] = useState(null);

  // State for register log validation data
  const [registerLogValidationData, setRegisterLogValidationData] =
    useState(null);

  // State for detail entries with infinite scroll
  const [detailEntries, setDetailEntries] = useState([]);
  const [detailEntriesTotal, setDetailEntriesTotal] = useState(0);
  const [detailEntriesPage, setDetailEntriesPage] = useState(0);
  const [hasMoreDetails, setHasMoreDetails] = useState(true);
  const [scrollFlagDetails, setScrollFlagDetails] = useState(false);

  // State for form field entries with infinite scroll
  const [formFieldEntries, setFormFieldEntries] = useState([]);
  const [formFieldEntriesTotal, setFormFieldEntriesTotal] = useState(0);
  const [formFieldEntriesPage, setFormFieldEntriesPage] = useState(0);
  const [hasMoreFormFields, setHasMoreFormFields] = useState(true);
  const [scrollFlagFormFields, setScrollFlagFormFields] = useState(false);

  // Query for GET_REGISTER
  const [getRegister] = useLazyQueryWithCallbacks(GET_REGISTER, {
    fetchPolicy: 'network-only',
    onError() {},
    onCompleted(response) {
      setRegisterData(response?.getRegister);
    },
  });

  // Query for registerLogDetailEntryList with infinite scroll
  const [getRegisterLogDetails, { loading: detailsLoading }] = useLazyQueryWithCallbacks(
    GET_REGISTER_LOG_DETAILS_ENTRY,
    {
      fetchPolicy: 'network-only',
      onError() {},
      onCompleted(res) {
        const data = res?.registerLogDetailEntryList?.data || [];
        const total = res?.registerLogDetailEntryList?.total || 0;

        setDetailEntriesTotal(total);

        if (scrollFlagDetails) {
          setDetailEntries((prev) => [...prev, ...data]);
        } else {
          setDetailEntries(data);
        }

        setHasMoreDetails(data?.length > 0);
        setScrollFlagDetails(false);
      },
    },
  );

  // Query for registerLogFormFieldEntryList with infinite scroll
  const [getRegisterLogFormFields, { loading: formFieldsLoading }] =
    useLazyQueryWithCallbacks(GET_REGISTER_LOG_FORM_FIELDS_ENTRY_LIST, {
      fetchPolicy: 'network-only',
      onError() {},
      onCompleted(res) {
        const data = res?.registerLogFormFieldEntryList?.data || [];
        const total = res?.registerLogFormFieldEntryList?.total || 0;

        setFormFieldEntriesTotal(total);

        if (scrollFlagFormFields) {
          setFormFieldEntries((prev) => [...prev, ...data]);
        } else {
          setFormFieldEntries(data);
        }

        setHasMoreFormFields(data?.length > 0);
        setScrollFlagFormFields(false);
      },
    });

  // Query for GET_REGISTER_LOG_TO_VALIDATE_APPROVAL
  const [getRegisterLogValidation, { loading: validationLoading }] =
    useLazyQueryWithCallbacks(GET_REGISTER_LOG_TO_VALIDATE_APPROVAL, {
      fetchPolicy: 'network-only',
      onError() {},
      onCompleted(response) {
        setRegisterLogValidationData(
          response?.getRegisterLogToValidateApproval,
        );
        // Fetch register data
        getRegister({ variables: { getRegisterId: registerId } });
        // Fetch detail entries
        getRegisterLogDetails({
          variables: {
            registerLogId: logId,
            filter: {
              limit: DEFAULT_PAGE_SIZE,
              skip: 0,
              isFromTodo: true,
            },
          },
        });

        // Fetch form field entries
        getRegisterLogFormFields({
          variables: {
            registerLogId: logId,
            filter: {
              limit: DEFAULT_PAGE_SIZE,
              skip: 0,
              isFromTodo: true,
            },
          },
        });
      },
    });

  // Initial data fetch
  useEffect(() => {
    if (registerId && logId) {
      // Fetch register log validation data
      getRegisterLogValidation({ variables: { registerLogId: logId } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerId, logId]);

  // Refetch function
  const refetch = () => {
    if (registerId && logId) {
      setDetailEntriesPage(0);
      setFormFieldEntriesPage(0);

      getRegister({ variables: { getRegisterId: registerId } });

      getRegisterLogValidation({ variables: { registerLogId: logId } });

      getRegisterLogDetails({
        variables: {
          registerLogId: logId,
          filter: {
            limit: DEFAULT_PAGE_SIZE,
            skip: 0,
          },
        },
      });

      getRegisterLogFormFields({
        variables: {
          registerLogId: logId,
          filter: {
            limit: DEFAULT_PAGE_SIZE,
            skip: 0,
          },
        },
      });
    }
  };

  // Handle scroll for detail entries
  const handleDetailsScroll = (e) => {
    const { target } = e;
    const { scrollTop, scrollHeight, offsetHeight } = target || {};
    const scrolledToBottom =
      (scrollTop + offsetHeight) / scrollHeight >= SCROLL_PERCENT;

    if (
      scrolledToBottom &&
      hasMoreDetails &&
      !detailsLoading &&
      !scrollFlagDetails
    ) {
      if (detailEntries?.length >= detailEntriesTotal) return;

      setScrollFlagDetails(true);
      const nextPage = detailEntriesPage + 1;
      setDetailEntriesPage(nextPage);

      getRegisterLogDetails({
        variables: {
          registerLogId: logId,
          filter: {
            limit: DEFAULT_PAGE_SIZE,
            skip: nextPage * DEFAULT_PAGE_SIZE,
          },
        },
      });
    }
  };

  // Handle scroll for form field entries
  const handleFormFieldsScroll = (e) => {
    const { target } = e;
    const { scrollTop, scrollHeight, offsetHeight } = target || {};
    const scrolledToBottom =
      (scrollTop + offsetHeight) / scrollHeight >= SCROLL_PERCENT;

    if (
      scrolledToBottom &&
      hasMoreFormFields &&
      !formFieldsLoading &&
      !scrollFlagFormFields
    ) {
      if (formFieldEntries?.length >= formFieldEntriesTotal) return;

      setScrollFlagFormFields(true);
      const nextPage = formFieldEntriesPage + 1;
      setFormFieldEntriesPage(nextPage);

      getRegisterLogFormFields({
        variables: {
          registerLogId: logId,
          filter: {
            limit: DEFAULT_PAGE_SIZE,
            skip: nextPage * DEFAULT_PAGE_SIZE,
          },
        },
      });
    }
  };

  // Transform form field entries to match expected format
  // The API returns data where each item has responses object with field names as keys
  // We need to flatten this to individual entries with registerFormFieldId and rowIndex
  const transformedFormFieldEntries =
    registerData && formFieldEntries
      ? formFieldEntries?.flatMap((item, index) => {
          if (!item?.responses) return [];

          // Convert responses object to array of entries
          return Object?.entries(item?.responses || {})?.map(
            ([fieldName, value]) => {
              // Find the field definition to get the field ID
              const field = registerData?.registerFormFields?.find(
                (f) => f?.name === fieldName,
              );

              return {
                id: item?.id,
                comments: item?.comments,
                registerFormFieldId: field?.id,
                value,
                responses: item?.responses, // Preserve the full responses object
                rowIndex: index, // Use the array index as rowIndex (0-based)
              };
            },
          );
        })
      : [];

  // Filter out deleted fields from registerData
  const filteredRegisterDetails =
    registerData?.registerDetails?.filter((detail) => !detail?.deletedAt) || [];

  const filteredRegisterFormFields =
    registerData?.registerFormFields?.filter((field) => !field?.deletedAt) ||
    [];

  // Construct data structure
  const data = registerData
    ? {
        status: registerLogValidationData?.status,
        levelName: registerLogValidationData?.currentApprovalName,
        levelNumber: registerLogValidationData?.currentApprovalLevel,
        unifiedApprovalHistory:
          registerLogValidationData?.unifiedApprovalHistory,
        registerLogHistories: registerLogValidationData?.registerLogHistories,
        register: {
          ...registerData,
          name: registerLogValidationData?.register?.name || registerData?.name,
          registerDetails: filteredRegisterDetails,
          registerFormFields: filteredRegisterFormFields,
        },
        project: { ...registerLogValidationData?.project },
        registerLog: {
          id: logId,
          createdAt: registerLogValidationData?.createdAt,
          submittedForApprovalAt:
            registerLogValidationData?.submittedForApprovalAt,
          createdByUser: registerLogValidationData?.submitter,
          registerDetailEntries: detailEntries,
          registerFormFieldEntries: transformedFormFieldEntries,
        },
      }
    : null;
  useEffect(() => {
    if (data && data?.status !== REGISTER_LOG_STATUS.APPROVAL_PENDING) {
      // setShowModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return (
    <>
      {showModal && (
        <RegisterApprovalPopUp
          showModal={showModal}
          setShowModal={setShowModal}
          data={data}
        />
      )}

      <RegisterApproveModal
        showModal={showApproveModal}
        setShowModal={setShowApproveModal}
        data={data}
        title={title}
        status={status}
        commentData={commentData}
        additionalComments={additionalComments}
        logId={logId}
      />

      <div className={clsx('approval-link', 'register-details')}>
        <div className="mb-24">
          <RegisterApprovalSummary data={data} loading={validationLoading} />
        </div>

        <div className="mb-24">
          <RegisterLogDetailsSection
            data={data}
            refetch={refetch}
            setCommentData={setCommentData}
            commentData={commentData}
            onScroll={handleDetailsScroll}
          />
        </div>

        <div className="mb-24">
          <RegisterLogEntriesSection
            data={data}
            refetch={refetch}
            setCommentData={setCommentData}
            commentData={commentData}
            onScroll={handleFormFieldsScroll}
          />
        </div>

        <div>
          <RegisterAdditionalComment
            data={data}
            refetch={refetch}
            setCommentData={setCommentData}
            commentData={commentData}
            setAdditionalComments={setAdditionalComments}
            additionalComments={additionalComments}
          />
        </div>

        {data?.status === REGISTER_LOG_STATUS.APPROVAL_PENDING && (
          <div className="d-flex justify-center">
            <Button
              shape="round"
              onClick={() => {
                if (
                  findIndex(
                    commentData?.comments,
                    (item) => item?.remark && item?.remark !== null,
                  ) === -1 &&
                  !additionalComments?.length > 0
                ) {
                  return toast({
                    message:
                      'If you want to REDO, please add at least one comment.',
                    type: 'error',
                  });
                }
                setShowApproveModal(true);
                setTitle('REDO');
                setStatus(REGISTER_LOG_STATUS.REDO);
              }}
            >
              Redo
            </Button>
            <Button
              shape="round"
              type="primary"
              onClick={() => {
                setShowApproveModal(true);
                setTitle('APPROVE');
                setStatus(REGISTER_LOG_STATUS.APPROVED);
              }}
            >
              Approve
            </Button>
          </div>
        )}
      </div>
    </>
  );
};

export default RegisterApproverLink;
